import { useCallback, useState, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { SampleTestDto } from '@/api';
import { Alert, TablePagination, useTableSort } from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import { toNumber } from '../../lib/format';
import { formatSort } from '../../lib/tableSort';
import { useReopenSampleTest } from './api/mutations';
import { useSampleTestsList } from './api/queries';
import {
  CloseSampleTestDialog,
  type SampleTestOutcome,
} from './CloseSampleTestDialog';
import { resolveSampleTestErrorMessage } from './lib/errors';
import {
  PAGE_SIZE,
  hasSampleTestsFilters,
  resolveSampleTestsSearch,
  type SampleTestsSearch,
} from './lib/searchParams';
import { SampleTestsFilters } from './SampleTestsFilters';
import { SampleTestsTable } from './SampleTestsTable';
import { ScheduleSampleTestDialog } from './ScheduleSampleTestDialog';

const ROUTE_ID = '/_app/sample-tests' as const;
const ROUTE_PATH = '/sample-tests' as const;

type OpenDialog =
  | { kind: 'schedule'; test: SampleTestDto }
  | { kind: 'close'; test: SampleTestDto; outcome: SampleTestOutcome };

/*
 * План тестирования образцов - «Проектная таблица» макета, но строки не ручные:
 * их заводит синхронизация с отгрузками образцов 1С, по тесту на товар
 * (plan/sample-tests). Менеджер переносит дату теста и ставит итог; ошибся -
 * возвращает тест в тестирование. Фильтры, сортировка и страница - в адресе.
 */
export const SampleTestsPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolveSampleTestsSearch(search);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);

  const setSearch = useCallback(
    (patch: Partial<SampleTestsSearch>, resetPage = true) => {
      void navigate({
        from: ROUTE_PATH,
        to: ROUTE_PATH,
        search: (previous: SampleTestsSearch) => ({
          ...previous,
          ...patch,
          ...(resetPage ? { page: undefined } : null),
        }),
      });
    },
    [navigate],
  );

  const { toggleSort } = useTableSort(resolved.sort, (next) =>
    setSearch({ sort: formatSort(next) }),
  );

  const { data, isPending, isError, error } = useSampleTestsList(resolved);
  const reopen = useReopenSampleTest();

  const onSchedule = useCallback(
    (test: SampleTestDto) => setDialog({ kind: 'schedule', test }),
    [],
  );
  const onCloseWith = useCallback(
    (test: SampleTestDto, outcome: SampleTestOutcome) =>
      setDialog({ kind: 'close', test, outcome }),
    [],
  );
  const { mutate: reopenTest } = reopen;
  const onReopen = useCallback(
    (test: SampleTestDto) => reopenTest({ path: { id: test.id } }),
    [reopenTest],
  );

  const hasFilters = hasSampleTestsFilters(resolved);
  const total = toNumber(data?.totalRecords ?? 0);
  const totalPages = Math.max(1, toNumber(data?.totalPages ?? 1));
  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);

  return (
    <>
      <Alert tone="info" className="mb-4">
        Строки появляются сами из проведённых отгрузок образцов в 1С - по строке
        на товар; дата теста - через две недели после отгрузки. К дате теста
        узнайте у клиента результат и поставьте итог.
      </Alert>

      <SampleTestsFilters
        resolved={resolved}
        hasFilters={hasFilters}
        onChange={(patch) => setSearch(patch)}
        onReset={() =>
          void navigate({ from: ROUTE_PATH, to: ROUTE_PATH, search: {} })
        }
      />

      {reopen.isError && (
        <Alert tone="error" className="mb-3.5">
          Не удалось вернуть в тестирование:{' '}
          {resolveSampleTestErrorMessage(reopen.error)}
        </Alert>
      )}

      {isError ? (
        <Alert tone="error">
          Не удалось загрузить план тестирования:{' '}
          {resolveApiErrorMessage(error)}.
        </Alert>
      ) : (
        <SampleTestsTable
          tests={data?.items ?? []}
          isLoading={isPending}
          sort={resolved.sort}
          onSortChange={toggleSort}
          emptyLabel={
            hasFilters
              ? 'По выбранным фильтрам тестов нет'
              : 'Тестов в работе нет: строки появятся сами, когда в 1С проведут отгрузку образцов'
          }
          footer={
            <TablePagination
              page={resolved.page}
              totalPages={totalPages}
              onPageChange={(page) =>
                setSearch({ page: page === 1 ? undefined : page }, false)
              }
              summary={
                total === 0
                  ? 'Ничего не найдено'
                  : `Показаны ${firstOnPage}-${lastOnPage} из ${total}`
              }
            />
          }
          onSchedule={onSchedule}
          onCloseWith={onCloseWith}
          onReopen={onReopen}
          reopeningId={reopen.isPending ? reopen.variables.path.id : undefined}
        />
      )}

      {dialog?.kind === 'schedule' && (
        <ScheduleSampleTestDialog
          test={dialog.test}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'close' && (
        <CloseSampleTestDialog
          test={dialog.test}
          outcome={dialog.outcome}
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
};
